import { resolveEnvironment, type Environment } from "./environment";

export const RETURN_TO_PARAM = "returnTo";

const STORAGE_KEY = "vellum.returnTo";

const APP_DOMAINS: Record<Environment, string> = {
  local: "local.vibestack.cloud",
  dev: "dev.vellum.health",
  prod: "vellum.health",
};

/**
 * A `returnTo` is only honored when it points back at one of our own apps —
 * otherwise a crafted `?returnTo=https://evil.com` would turn the login flow
 * into an open redirect.
 */
export function isAllowedReturnTo(candidate: string): boolean {
  let url: URL;
  try {
    url = new URL(candidate, window.location.href);
  } catch {
    return false;
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return false;
  }

  const environment = resolveEnvironment();
  if (environment === "local" && url.hostname === "localhost") {
    return true;
  }

  const domain = APP_DOMAINS[environment];
  return url.hostname === domain || url.hostname.endsWith(`.${domain}`);
}

/** Adds the current page as `?returnTo=` on a login URL (e.g. the Hub). */
export function withReturnTo(
  loginUrl: string,
  returnTo: string = window.location.href,
): string {
  const url = new URL(loginUrl, window.location.href);
  url.searchParams.set(RETURN_TO_PARAM, returnTo);
  return url.toString();
}

/**
 * Stashes a `?returnTo=` from the current URL so it survives the Google OAuth
 * round trip, which lands back on this origin without our query string.
 */
export function stashReturnTo(): void {
  const candidate = new URLSearchParams(window.location.search).get(
    RETURN_TO_PARAM,
  );
  if (candidate && isAllowedReturnTo(candidate)) {
    window.sessionStorage.setItem(STORAGE_KEY, candidate);
    return;
  }

  // A logged-out visit with no usable `returnTo` supersedes whatever an
  // abandoned login attempt left behind, so drop the stale value rather than
  // letting it hijack this login.
  window.sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Reads and clears the stashed `returnTo`. Clearing on read keeps a stale
 * value from hijacking a later login.
 */
export function consumeReturnTo(): string | null {
  const stashed = window.sessionStorage.getItem(STORAGE_KEY);
  if (stashed === null) {
    return null;
  }

  window.sessionStorage.removeItem(STORAGE_KEY);

  if (!isAllowedReturnTo(stashed)) {
    return null;
  }

  // Already where the user wanted to be — redirecting would just be a second
  // full page load of the same URL.
  const target = new URL(stashed, window.location.href).toString();
  return target === window.location.href ? null : target;
}
